import { OrniTransportError } from "./transport.ts";
import {
  decodeAgentIdentity,
  decodeProviderId,
  type AgentId,
  type CliVariantId,
  type LegacyProviderId,
  type ProviderId,
} from "./agent-protocol.ts";

export interface ProviderTerminalQuickCommand {
  /** Exact text to inject into the Provider's native terminal composer. */
  command: string;
  /** Provider-configured mobile interaction behavior. */
  execution: ProviderTerminalCommandExecution;
  /** Optional, display-only argument hint. */
  argumentHint: string | null;
  /** Deprecated compatibility projection for pre-mode clients. */
  autoSubmit: boolean;
}

export type ProviderTerminalCommandExecution = "draft" | "submit" | "interactive" | "shell";

export interface ProviderInventoryItem {
  providerId: ProviderId;
  /** Canonical Agent identity projected from the legacy provider DTO. */
  agentId: AgentId;
  /** Native executable variant selected by the trusted adapter. */
  cliVariantId: CliVariantId;
  legacyProviderId: LegacyProviderId | null;
  adapterId: string | null;
  adapterVersion: string | null;
  displayName: string;
  /** Registry candidate metadata; candidate rows are read-only until a
   * verified execution adapter is promoted. */
  candidate: boolean;
  installed: boolean;
  enabled: boolean;
  supportsNative: boolean;
  supportsOrniWork: boolean;
  terminalQuickCommands: ProviderTerminalQuickCommand[];
  executablePath: string | null;
  configuredExecutablePath: string | null;
  version: string | null;
  homeDirectory: string;
  configuredHomeDirectory: string | null;
  apiBaseUrl: string | null;
  /** Explicit API Manager protocol identity, when bound to this CLI. */
  protocolId: string | null;
  /** Explicit API Manager auth kind; never inferred from the URL. */
  authKind: string | null;
  apiKeyConfigured: boolean;
  defaultModel: string | null;
  installCommand: string;
  upgradeCommand: string;
  lastCheckedAt: string;
  revision: number;
}

export interface ProviderInventory {
  providers: ProviderInventoryItem[];
  checkedAt: string;
}

/** Device-owned settings mutation shared by local and Cloud transports. */
export interface ProviderSettingsInput {
  enabled: boolean;
  executablePath?: string | null;
  homeDirectory?: string | null;
  apiBaseUrl?: string | null;
  protocolId?: string | null;
  authKind?: string | null;
  clearCredentialMode?: boolean;
  apiKey?: string;
  clearApiKey?: boolean;
  defaultModel?: string | null;
  expectedRevision?: number;
}

/** Decode the shared Daemon/Cloud Provider inventory at the transport boundary. */
export function decodeProviderInventory(value: unknown): ProviderInventory {
  const root = record(value, "ProviderInventory");
  return {
    providers: array(field(root, "providers"), "ProviderInventory.providers").map(
      (value, index) => decodeProvider(value, `ProviderInventory.providers[${index}]`),
    ),
    checkedAt: text(field(root, "checked_at", "checkedAt"), "ProviderInventory.checked_at"),
  };
}

function decodeProvider(value: unknown, path: string): ProviderInventoryItem {
  const row = record(value, path);
  const identity = decodeAgentIdentity(row, path);
  const quickCommandsValue = field(
    row,
    "terminal_quick_commands",
    "terminalQuickCommands",
  );
  return {
    providerId: decodeProviderId(field(row, "provider_id", "providerId"), `${path}.provider_id`),
    agentId: identity.agentId,
    cliVariantId: identity.cliVariantId,
    legacyProviderId: identity.legacyProviderId,
    adapterId: nullableText(field(row, "adapter_id", "adapterId"), `${path}.adapter_id`),
    adapterVersion: nullableText(field(row, "adapter_version", "adapterVersion"), `${path}.adapter_version`),
    displayName: text(field(row, "display_name", "displayName"), `${path}.display_name`),
    candidate: field(row, "candidate") === undefined
      ? false
      : boolean(field(row, "candidate"), `${path}.candidate`),
    installed: boolean(field(row, "installed"), `${path}.installed`),
    enabled: boolean(field(row, "enabled"), `${path}.enabled`),
    supportsNative: boolean(field(row, "supports_native", "supportsNative"), `${path}.supports_native`),
    supportsOrniWork: boolean(
      field(row, "supports_orni_work", "supportsOrniWork"),
      `${path}.supports_orni_work`,
    ),
    terminalQuickCommands: quickCommandsValue === undefined
      ? []
      : array(quickCommandsValue, `${path}.terminal_quick_commands`).map(
        (command, index) => decodeQuickCommand(command, `${path}.terminal_quick_commands[${index}]`),
      ),
    executablePath: nullableText(field(row, "executable_path", "executablePath"), `${path}.executable_path`),
    configuredExecutablePath: nullableText(
      field(row, "configured_executable_path", "configuredExecutablePath"),
      `${path}.configured_executable_path`,
    ),
    version: nullableText(field(row, "version"), `${path}.version`),
    homeDirectory: text(field(row, "home_directory", "homeDirectory"), `${path}.home_directory`),
    configuredHomeDirectory: nullableText(
      field(row, "configured_home_directory", "configuredHomeDirectory"),
      `${path}.configured_home_directory`,
    ),
    apiBaseUrl: nullableText(field(row, "api_base_url", "apiBaseUrl"), `${path}.api_base_url`),
    protocolId: nullableText(field(row, "protocol_id", "protocolId"), `${path}.protocol_id`),
    authKind: nullableText(field(row, "auth_kind", "authKind"), `${path}.auth_kind`),
    apiKeyConfigured: boolean(
      field(row, "api_key_configured", "apiKeyConfigured"),
      `${path}.api_key_configured`,
    ),
    defaultModel: nullableText(field(row, "default_model", "defaultModel"), `${path}.default_model`),
    installCommand: text(field(row, "install_command", "installCommand"), `${path}.install_command`),
    upgradeCommand: text(field(row, "upgrade_command", "upgradeCommand"), `${path}.upgrade_command`),
    lastCheckedAt: text(field(row, "last_checked_at", "lastCheckedAt"), `${path}.last_checked_at`),
    revision: nonNegativeInteger(field(row, "revision"), `${path}.revision`),
  };
}

function decodeQuickCommand(value: unknown, path: string): ProviderTerminalQuickCommand {
  const row = record(value, path);
  const command = text(field(row, "command"), `${path}.command`);
  if (command.length > 96 || !(/^(?:\/[a-z][a-z0-9-]*(?:[ \t]+[A-Za-z0-9._:\/@+-]+)*|!)$/.test(command))) {
    throw contractError(`${path}.command is not a safe Provider slash command.`);
  }
  // Older Daemons only sent `command`. Complete commands were historically
  // submitted by clients, while `/skills` opens an interactive picker. Keep
  // that behavior when the capability field is absent.
  const autoSubmitValue = field(row, "auto_submit", "autoSubmit");
  const autoSubmit = autoSubmitValue === undefined
    ? command !== "/skills"
    : boolean(autoSubmitValue, `${path}.auto_submit`);
  const executionValue = field(row, "execution");
  const execution = executionValue === undefined
    ? (autoSubmit ? "submit" : "interactive")
    : decodeQuickCommandExecution(executionValue, path + ".execution");
  const argumentHintValue = field(row, "argument_hint", "argumentHint");
  const argumentHint = argumentHintValue === undefined || argumentHintValue === null
    ? null
    : text(argumentHintValue, path + ".argument_hint");
  if (argumentHint !== null && argumentHint.length > 120) {
    throw contractError(path + ".argument_hint is too long.");
  }
  return {
    command,
    execution,
    argumentHint,
    autoSubmit: executionValue === undefined ? autoSubmit : execution === "submit",
  };
}

function decodeQuickCommandExecution(value: unknown, path: string): ProviderTerminalCommandExecution {
  if (value === "draft" || value === "submit" || value === "interactive" || value === "shell") return value;
  throw contractError(path + " is not a supported terminal command execution mode.");
}

function field(
  row: Record<string, unknown>,
  snakeCase: string,
  camelCase = snakeCase,
): unknown {
  return row[snakeCase] ?? row[camelCase];
}

function record(value: unknown, path: string): Record<string, unknown> {
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    throw contractError(`${path} must be an object.`);
  }
  return value as Record<string, unknown>;
}

function array(value: unknown, path: string): unknown[] {
  if (!Array.isArray(value)) throw contractError(`${path} must be an array.`);
  return value;
}

function text(value: unknown, path: string): string {
  if (typeof value !== "string") throw contractError(`${path} must be text.`);
  return value;
}

function nullableText(value: unknown, path: string): string | null {
  return value === null || value === undefined ? null : text(value, path);
}

function boolean(value: unknown, path: string): boolean {
  if (typeof value !== "boolean") throw contractError(`${path} must be boolean.`);
  return value;
}

function nonNegativeInteger(value: unknown, path: string): number {
  if (!Number.isSafeInteger(value) || Number(value) < 0) {
    throw contractError(`${path} must be a non-negative integer.`);
  }
  return Number(value);
}

function contractError(message: string): OrniTransportError {
  return new OrniTransportError("CONTRACT_INVALID", message);
}
